import { useState, useRef, useEffect } from "react";
import { fetchEmployeeProfilePicAPI } from "../services/employeeService";

/**
 * 🖼️ useEmployeeProfilePics Hook
 * يدير صور الموظفين والـ caching والـ loading states
 */
export function useEmployeeProfilePics(employees = []) {
  const [employeeProfilePics, setEmployeeProfilePics] = useState({});
  const [loadingPics, setLoadingPics] = useState(new Set());
  const picCacheRef = useRef(new Map());

  // ============== FETCH SINGLE PROFILE PICTURE ==============
  const fetchProfilePic = async (infinityCode) => {
    return fetchEmployeeProfilePicAPI(infinityCode, picCacheRef, loadingPics, setLoadingPics);
  };

  // ============== FETCH ALL PROFILE PICTURES IN BATCHES ==============
  useEffect(() => {
    const fetchAllProfilePics = async () => {
      if (employees.length === 0) return;

      const missingPics = employees
        .filter(emp => !employeeProfilePics[emp.id] && !picCacheRef.current.has(emp.id))
        .map(emp => emp.id);

      if (missingPics.length === 0) return;

      console.log(`📸 Fetching ${missingPics.length} profile pictures...`);

      const BATCH_SIZE = 5;
      const newPics = {};

      for (let i = 0; i < missingPics.length; i += BATCH_SIZE) {
        const batch = missingPics.slice(i, i + BATCH_SIZE);

        const results = await Promise.all(
          batch.map(async (id) => {
            try {
              const url = await fetchProfilePic(id);
              return { id, url };
            } catch (err) {
              console.error(`❌ Failed to load pic for ${id}:`, err.message);
              return { id, url: null };
            }
          })
        );

        results.forEach(({ id, url }) => {
          if (url) {
            newPics[id] = url;
          }
        });

        if (i + BATCH_SIZE < missingPics.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      if (Object.keys(newPics).length > 0) {
        setEmployeeProfilePics(prev => ({ ...prev, ...newPics }));
        console.log(`✅ Loaded ${Object.keys(newPics).length} profile pictures!`);
      }
    };

    fetchAllProfilePics();
  }, [employees]);

  // ============== CLEANUP ON UNMOUNT ==============
  useEffect(() => {
    return () => {
      // Revoke all URLs to prevent memory leaks
      Object.values(employeeProfilePics).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });

      picCacheRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });

      picCacheRef.current.clear();
    };
  }, []);

  return {
    employeeProfilePics,
    setEmployeeProfilePics,
    loadingPics,
    setLoadingPics,
    picCacheRef,
    fetchProfilePic,
  };
}
